import React, { useEffect, useState } from 'react';
import { Alert, Tag, Tooltip, Typography } from 'antd';
import {
  CubeIcon, GitBranchIcon, GithubLogoIcon, GitlabLogoIcon, KanbanIcon, KeyIcon, PackageIcon, SlackLogoIcon, WrenchIcon,
} from '@phosphor-icons/react';
import { ACCOUNT_TYPES, STATUS } from './accountTypes';
import { updatedAgo } from './useAccounts';
import { formatRelativeTime } from '../../utils/relativeTime';

const ICONS = {
  github: GithubLogoIcon,
  gitlab: GitlabLogoIcon,
  bitbucket: GitBranchIcon,
  'bitbucket-dc': GitBranchIcon,
  'azure-devops': GitBranchIcon,
  gitea: GitBranchIcon,
  npm: PackageIcon,
  pypi: PackageIcon,
  maven: PackageIcon,
  oci: CubeIcon,
  atlassian: KanbanIcon,
  slack: SlackLogoIcon,
};
const CATEGORY_ICONS = { git: GitBranchIcon, registry: PackageIcon, tool: WrenchIcon };

/** The type's logo, or a generic icon for its category (`category` helps for a type only the catalog knows). */
export function ProviderIcon({ type, category, size = 18 }) {
  const Icon = ICONS[type] || CATEGORY_ICONS[category || ACCOUNT_TYPES[type]?.category] || KeyIcon;
  return <Icon aria-hidden size={size} />;
}

const expiryText = (account) => {
  if (account.method === 'oauth') { return 'Refreshed automatically'; }
  return account.expiresAt ? `Expires ${new Date(account.expiresAt).toLocaleDateString()}` : 'Does not expire';
};

export function StatusTag({ account }) {
  const status = STATUS[account.status] || STATUS.untested;
  const test = account.lastTest;
  const title = (
    <div>
      <div>{test ? `Last test ${test.ok ? 'passed' : 'failed'} ${formatRelativeTime(test.at)}: ${test.message}` : 'Never tested: press Test.'}</div>
      {test?.hint && <div>Fix: {test.hint}</div>}
      <div>{expiryText(account)}</div>
    </div>
  );
  return (
    <Tooltip title={title}>
      <Tag color={status.color} style={{ marginInlineEnd: 0 }}>{status.label}</Tag>
    </Tooltip>
  );
}

export { expiryText };

/** The outcome of a test, with a copyable fix when there is one. */
export function TestResult({ outcome, title, onClose }) {
  if (!outcome) { return null; }
  const heading = title || (outcome.ok ? (outcome.hint ? 'Connected, with a least-privilege warning' : 'Connected') : 'Connection test failed');
  return (
    <Alert
      closable={Boolean(onClose)}
      description={(
        <div>
          <div>{outcome.message}</div>
          {outcome.hint && (
            <Typography.Paragraph className="acct-hint" copyable={{ text: outcome.hint }} style={{ marginBottom: 0, marginTop: 6 }}>
              {outcome.hint}
            </Typography.Paragraph>
          )}
        </div>
      )}
      onClose={onClose}
      showIcon
      title={heading}
      type={outcome.ok ? (outcome.hint ? 'warning' : 'success') : 'error'}
    />
  );
}

const BANNER = {
  connected: { type: 'success', title: 'OpenBao connected' },
  sealed: { type: 'error', title: 'OpenBao is sealed' },
  uninitialized: { type: 'error', title: 'OpenBao is not initialized' },
  unreachable: { type: 'error', title: 'Your accounts store is unreachable' },
  denied: { type: 'error', title: 'OpenBao refused provider-token-service' },
  off: { type: 'warning', title: 'Accounts are not enabled on this Flow' },
};

/**
 * The store's health: `state` is the store's (connected, sealed, uninitialized, unreachable, denied) or `off` when
 * Flow has no accounts service. Compact while connected.
 */
export function StoreBanner({ state, detail, fix }) {
  const banner = BANNER[state] || BANNER.unreachable;
  if (state === 'connected') {
    return <div className="acct-store-ok" role="status"><span className="acct-dot" aria-hidden /> {banner.title}{detail ? ` · ${detail}` : ''}</div>;
  }
  return (
    <Alert
      description={(
        <div>
          {detail && <div>{detail}</div>}
          {fix && <Typography.Paragraph className="acct-hint" copyable={{ text: fix }} style={{ marginBottom: 0, marginTop: 6 }}>{fix}</Typography.Paragraph>}
        </div>
      )}
      showIcon
      title={banner.title}
      type={banner.type}
    />
  );
}

/** "Updated 5 s ago · live": click to read now. */
export function LiveIndicator({ updatedAt, failures, hidden, onRefresh }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const state = hidden ? 'idle' : failures > 0 ? 'retrying' : 'live';
  const label = { idle: 'waiting for the tab', live: 'live', retrying: 'retrying' }[state];
  const tip = {
    idle: 'Updates resume when this tab is visible again.',
    live: 'This list keeps itself current. Click to update now.',
    retrying: `The last ${failures} update${failures === 1 ? '' : 's'} failed: retrying, a little less often each time. Click to try now.`,
  }[state];
  return (
    <Tooltip title={tip}>
      <button className="acct-live" data-state={state} onClick={onRefresh} type="button">
        <span aria-hidden className="acct-live-dot" />
        <span>{updatedAt === undefined ? 'Loading' : `Updated ${updatedAgo(updatedAt, now)}`} · {label}</span>
      </button>
    </Tooltip>
  );
}
